// Servicios para los centros médicos (proveedores)

import { getAuthHeader } from '@/utils';
import { BASE_URL } from '@core/constants';

/**
 * @typedef MedicalCenterFund
 * @property {number} medical_center
 * @property {string} medical_center_name
 * @property {number} consumed
 * @property {number} available
 * @property {number} number_atention
 * @property {string} amount_advance
 * @property {string} amount_incurred
 * @property {string} amount_available
 * @property {number} amount_available_ref
 * @property {boolean} is_sub_administrator
 * @property {string} client_name
 * @property {boolean} is_insolvent
 */

/**
 * @typedef MedicalCenter
 * @property {number} id
 * @property {string} name
 * @property {string} total_advance
 * @property {string} total_incurred
 * @property {number} available
 * @property {number} consumed
 * @property {number} number_atention
 * @property {MedicalCenterFund} fund
 * @property {string} amount_advance
 * @property {string} amount_incurred
 * @property {string} amount_available
 * @property {number} amount_available_ref
 * @property {boolean} is_sub_administrator
 */

/**
 * @typedef {MedicalCenter & {
 *   fund_limit: number | null;
 *   amount_advance: number;
 *   amount_incurred: number;
 * }} ManagedMedicalCenter
 */

/**
 * @typedef CenterRequirementDay
 * @property {"lunes" | "martes" | "miércoles" | "jueves" | "viernes" | "sábado" | "domingo"} day
 * @property {number} quota
 */

/**
 * @typedef CompanyMedicalCenterRequirement
 * @property {number} id
 * @property {number} company_client
 * @property {number} requirement
 * @property {number} requirement_price_ref
 * @property {string} company_client_name
 * @property {string} requirement_name
 * @property {string} type_requirement_name
 * @property {CenterRequirementDay[]} requirement_day
 * @property {number} medical_center
 * @property {string} medical_center_name
 */

/**
 * Si no se especifica ningún parámetro, devuelve el logo del centro médico del usuario autenticado.
 * @param {*} centerId Si se especifica devuelve el logo del centro (tiene prioridad sobre studyId)
 * @param {*} studyId  Si se especifica devuelve el logo del centro asociado al estudio
 */
export async function getMedicalCenterLogo({ centerId = null, studyId = null } = {}) {
  try {
    const params = new URLSearchParams();
    if (centerId) params.append('medical_center_id', centerId);
    if (studyId) params.append('study_id', studyId); // ← CAMBIAR 'study' POR 'study_id'

    const res = await fetch(`${BASE_URL}insurance/medical-center/logo/get/?${params.toString()}`, {
      headers: getAuthHeader(),
    });
    if (res.ok) {
      const data = await res.json();
      return data.logo;
    }
    console.error('Error fetching medical center logo', res);
    return null;
  } catch (err) {
    console.error('Error al cargar logo:', err);
    return null;
  }
}

export async function setMedicalCenterLogo({ centerId = null, logoFile }) {
  try {
    if (!logoFile) throw new Error('No se proporcionó ningún archivo de logo');
    if (!logoFile.type.startsWith('image/'))
      throw new Error('Solo se permiten archivos de imagen (JPEG, PNG, GIF)');
    if (logoFile.size > 2 * 1024 * 1024) throw new Error('La imagen no puede ser mayor a 2MB');

    const form = new FormData();
    form.append('logo', logoFile);

    const params = new URLSearchParams();
    if (centerId) params.append('medical_center_id', centerId);

    const res = await fetch(`${BASE_URL}insurance/medical-center/logo/?${params.toString()}`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: form,
    });

    if (res.ok) {
      const data = await res.json();
      return data.logo;
    }
    console.error('Error al actualizar logo:', res);
    return null;
  } catch (err) {
    console.error('Error al actualizar logo:', err);
    return null;
  }
}

/**
 * Get list of medical centers
 */
export async function getMedicalCenters({
  clientID,
  subAdministrator,
  isSubAdministrator = null,
  managerCenters = null,
}) {
  const params = new URLSearchParams();
  if (clientID) {
    params.append('client', clientID);
  }
  if (subAdministrator) {
    params.append('sub_administrators', subAdministrator);
  }
  if (isSubAdministrator !== null) {
    params.append('is_sub_administrator', String(isSubAdministrator));
  }
  if (managerCenters) {
    params.append('manager_centers', managerCenters);
  }

  try {
    const response = await fetch(`${BASE_URL}insurance/medical-center/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
    });

    const data = await response.json();

    /** @type MedicalCenter[] */
    const results = data.results;
    return results;
  } catch (error) {
    console.error('Error fetching medical centers', error);
    return [];
  }
}

/**
 * Gets singular medical center
 * @param {number} centerId
 */
export async function getMedicalCenter(centerId) {
  try {
    const response = await fetch(`${BASE_URL}insurance/medical-center/${centerId}/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }
    /**@type {MedicalCenter} */
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching medical center', error);
    return null;
  }
}
/**
 * Gets singular medical center
 * @param {number} centerId
 */
export async function updateMedicalCenter({ centerId, data, logoFile = null }) {
  try {
    const safeData = { ...(data || {}) };
    // Backend serializer expects `client` as a list (many=True).
    if (Object.prototype.hasOwnProperty.call(safeData, 'client')) {
      const rawClient = safeData.client;
      if (Array.isArray(rawClient)) {
        safeData.client = rawClient;
      } else if (rawClient === null || rawClient === undefined || rawClient === '') {
        safeData.client = [];
      } else {
        safeData.client = [rawClient];
      }
    }
    // Avoid sending ImageField as plain string/url; backend expects multipart file for `logo`
    delete safeData.logo;

    // If a logo file is provided, send multipart/form-data so backend can save ImageField
    if (logoFile) {
      const form = new FormData();
      // Append all provided data fields
      if (safeData && typeof safeData === 'object') {
        Object.keys(safeData).forEach(k => {
          const v = safeData[k];
          if (v === undefined || v === null) return;
          if (k === 'logo') return;
          if (Array.isArray(v)) {
            form.append(k, JSON.stringify(v));
          } else if (typeof v === 'object') {
            form.append(k, JSON.stringify(v));
          } else {
            form.append(k, String(v));
          }
        });
      }
      form.append('logo', logoFile);

      const headers = { ...(getAuthHeader() || {}) };

      const response = await fetch(`${BASE_URL}insurance/medical-center/${centerId}/`, {
        method: 'PATCH',
        headers,
        body: form,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    }

    // Fallback: JSON patch
    const response = await fetch(`${BASE_URL}insurance/medical-center/${centerId}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(safeData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${response.status}`);
    }
    const responseData = await response.json().catch(() => null);
    return responseData || { type: 'success', message: 'Proveedor actualizado con éxito' };
  } catch (error) {
    console.error('Error updating medical center', error);
    throw error;
  }
}

/**
 * Delete a medical center
 * @param {number} centerId
 */
export async function deleteMedicalCenter(centerId) {
  try {
    const response = await fetch(`${BASE_URL}insurance/medical-center/${centerId}/`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeader(),
      },
    });

    if (response.status === 204 || response.ok) {
      return { type: 'success', message: 'Centro eliminado' };
    }

    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      // ignore
    }

    return { type: 'error', message: (data && data.message) || `HTTP ${response.status}` };
  } catch (error) {
    console.error('Error deleting medical center', error);
    return { type: 'error', message: String(error) };
  }
}

/**
 * Get list of requirements from medical center
 * @param {number} centerId
 */
export async function getRequirementsFromCenter(centerId) {
  const response = await fetch(
    `${BASE_URL}insurance/company-medical-center-requirement/?medical_center=${centerId}`,
    {
      headers: {
        ...getAuthHeader(),
      },
    }
  );
  if (!response.ok) {
    throw new Error(JSON.stringify(response));
  }
  /**@type {CompanyMedicalCenterRequirement[]} */
  const data = await response.json();
  return data;
}

/**
 * Get requirements of a type from medical center, available for a client
 * @param {object} params
 * @param {number} params.centerId
 * @param {number} params.client
 * @param {number} params.requirementType
 * @param {number?} params.subAdministrator
 */
/**
 * El catálogo centro↔cliente (CompanyRequirementMedicalCenter) puede tener
 * filas repetidas para el mismo requerimiento (la tabla no tiene restricción
 * única). Se conserva solo la primera fila de cada requerimiento para que no
 * aparezca duplicado en los selectores.
 * @param {CompanyMedicalCenterRequirement[]} rows
 */
function uniqueByRequirement(rows) {
  const seen = new Set();
  return rows.filter(row => {
    const requirementId = Number(row.requirement);
    if (seen.has(requirementId)) return false;
    seen.add(requirementId);
    return true;
  });
}

export async function getClientRequirementsFromCenter({
  centerId,
  client,
  requirementType,
  state,
  subAdministrator,
}) {
  // Validation
  if (!centerId || (!client && !subAdministrator)) {
    return [];
  }

  // Create query parameters
  const queryParams = new URLSearchParams();
  if (centerId) {
    console.log('centerID', centerId);
    queryParams.append('medical_center', centerId);
  }
  if (client) {
    queryParams.append('company_client', client);
  }
  if (requirementType) {
    queryParams.append('requirement__type_requirement', requirementType);
  }
  if (state) {
    queryParams.append('state', state);
  }
  if (subAdministrator) {
    queryParams.append('sub_administrator', subAdministrator);
  }

  // Fetch data
  const response = await fetch(
    `${BASE_URL}insurance/company-medical-center-requirement/?${queryParams.toString()}`,
    {
      headers: {
        ...getAuthHeader(),
      },
    }
  );
  if (!response.ok) {
    return [];
  }
  /**@type {CompanyMedicalCenterRequirement[]} */
  const data = await response.json();
  if (!data || !Array.isArray(data)) {
    return [];
  }

  // Extract requirements and their names from data array
  const requirements = uniqueByRequirement(data).map(companyMedicalCenterRequirement => ({
    id: Number(companyMedicalCenterRequirement.requirement),
    name: String(companyMedicalCenterRequirement.requirement_name),
  }));

  return requirements;
}

/**
 * Get requirements of a type from medical center, available for a client
 * @param {object} params
 * @param {number} params.centerId
 * @param {number} params.client
 * @param {number} params.requirementType
 * @param {number?} params.subAdministrator
 */
export async function getClientRequirementsListFromCenter({
  centerId,
  client,
  requirementType,
  state,
  subAdministrator,
}) {
  // Validation
  if (!centerId || (!client && !subAdministrator)) {
    return [];
  }

  // Create query parameters
  const queryParams = new URLSearchParams();
  if (centerId) {
    console.log('centerID', centerId);
    queryParams.append('medical_center', centerId);
  }
  if (client) {
    queryParams.append('company_client', client);
  }
  if (requirementType) {
    queryParams.append('requirement__type_requirement', requirementType);
  }
  if (state) {
    queryParams.append('state', state);
  }
  if (subAdministrator) {
    queryParams.append('sub_administrator', subAdministrator);
  }

  // Fetch data
  const response = await fetch(
    `${BASE_URL}insurance/company-medical-center-requirement-list/?${queryParams.toString()}`,
    {
      headers: {
        ...getAuthHeader(),
      },
    }
  );
  if (!response.ok) {
    return [];
  }
  /**@type {CompanyMedicalCenterRequirement[]} */
  const data = await response.json();
  if (!data || !Array.isArray(data)) {
    return [];
  }

  // Extract requirements and their names from data array
  const requirements = uniqueByRequirement(data).map(companyMedicalCenterRequirement => ({
    id: Number(companyMedicalCenterRequirement.requirement),
    name: String(companyMedicalCenterRequirement.requirement_name),
    ...companyMedicalCenterRequirement,
  }));

  return requirements;
}

/**
 * Get list of medical centers that offer at least one of the given requirements
 * @param {object} params
 * @param {number[]} params.requirements
 * @param {number?} params.client
 */
export async function getCentersFromRequirements({
  requirements = [],
  client,
  state,
  subAdministrator = null,
}) {
  try {
    // Create query params
    const params = new URLSearchParams();
    requirements.forEach(requirement => params.append('requirement', requirement));
    if (client) {
      params.append('company_client', client);
    }
    if (state) {
      params.append('state', state);
    }
    if (subAdministrator) {
      params.append('sub_administrator', subAdministrator);
    }

    // Fetch data
    const response = await fetch(
      `${BASE_URL}insurance/company-medical-center-requirement/?${params.toString()}`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }

    /**@type {CompanyMedicalCenterRequirement[]} */
    const data = await response.json();

    // Handle empty response
    if (!data || !Array.isArray(data)) {
      return [];
    }

    /**
     * Data will be a list of `company-medical-center-requirement`s
     *
     * Group by medical center
     *
     * New struct will have:
     * - medical_center: ID of the medical center
     * - medical_center_name: Name of the medical center
     * @type {{ medical_center: number, medical_center_name: string}[]}
     */
    const formattedData = [];
    data.forEach(centerRequirement => {
      const medicalCenterId = Number(centerRequirement.medical_center);
      if (!formattedData.some(center => Number(center.medical_center) === medicalCenterId)) {
        formattedData.push({
          medical_center: medicalCenterId,
          medical_center_name: centerRequirement.medical_center_name,
        });
      }
    });

    return formattedData;
  } catch (error) {
    console.error('Error fetching medical centers from requirements', error);
    return [];
  }
}

/**
 * Add medical center
 * @param {object} params
 * @param {string} params.name
 * @param {import("../MedicalCenters/CreateMedicalCenter").CenterRequirementSlot} params.requirements
 */
export async function addMedicalCenter({
  name,
  client,
  phone_number,
  phone_number_admin,
  address,
  rif,
  currency,
  state,
  is_sub_administrator = null,
  fund_type = null,
  managed_centers = null,
  logoFile = null,
}) {
  if (!name || !name.trim()) {
    throw new Error('El nombre es obligatorio');
  }

  // If a logo file is provided, send multipart/form-data so the backend
  // Create view (now supporting multipart) can save the ImageField.
  if (logoFile) {
    const form = new FormData();
    form.append('name', name.trim());
    if (client !== undefined && client !== null) form.append('client', JSON.stringify(client));
    if (phone_number !== undefined && phone_number !== null)
      form.append('phone_number', String(phone_number).trim());
    if (phone_number_admin !== undefined && phone_number_admin !== null)
      form.append('phone_number_admin', String(phone_number_admin).trim());
    if (address !== undefined && address !== null) form.append('address', String(address).trim());
    if (rif !== undefined && rif !== null) form.append('rif', String(rif).trim());
    if (currency !== undefined && currency !== null) form.append('currency', String(currency));
    if (state !== undefined && state !== null) form.append('state', JSON.stringify(state));
    if (is_sub_administrator !== undefined && is_sub_administrator !== null)
      form.append('is_sub_administrator', String(Boolean(is_sub_administrator)));
    if (fund_type !== undefined && fund_type !== null) form.append('fund_type', String(fund_type));
    if (managed_centers !== undefined && managed_centers !== null)
      form.append('managed_medical_centers', JSON.stringify(managed_centers));
    form.append('logo', logoFile);

    const headers = { ...(getAuthHeader() || {}) };

    const response = await fetch(`${BASE_URL}insurance/medical-center/`, {
      method: 'POST',
      headers,
      body: form,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Error al crear el centro médico');
    }

    return await response.json();
  }

  // Fallback: send JSON when no file provided (backwards compatible)
  const response = await fetch(`${BASE_URL}insurance/medical-center/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({
      name: name.trim(),
      client,
      phone_number: phone_number?.trim(),
      phone_number_admin: phone_number_admin?.trim(),
      address: address?.trim(),
      rif: rif?.trim(),
      currency,
      state,
      is_sub_administrator,
      fund_type,
      managed_medical_centers: managed_centers,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Error al crear el centro médico');
  }

  return await response.json(); // Devuelve el centro creado
}

/**
 * Associates a requirement to medical center
 * @param {object} params
 * @param {{ id: number, name: string }} params.client Client that the medical center will serve
 * @param {import("../MedicalCenters/CreateMedicalCenter").SlotRequirement[]} params.requirements Requirements to associate
 * @param {number} params.centerId ID of the medical center
 */
export async function updateMedicalCenterRequirement({ client, requirements, centerId }) {
  try {
    // Validations
    if (!client) {
      throw new Error('Especifique el cliente que servirá');
    }
    if (!requirements || requirements.length === 0) {
      throw new Error('Debe especificar al menos un requerimiento');
    }

    // Format days into backend accepted structure
    let formattedDays = [];
    if (requirements[0].days) {
      formattedDays = requirements[0].days.map(day => ({
        day: day.apiLabel,
        quota: day.slots,
      }));
    }

    await Promise.allSettled(
      requirements.map(req => {
        return fetch(`${BASE_URL}insurance/company-medical-center-requirement/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
          body: JSON.stringify({
            company_client: client.id,
            requirement: req.requirement,
            requirement_day: formattedDays,
            medical_center: centerId,
          }),
        });
      })
    );

    return {
      type: 'success',
      message: 'Requerimientos asociados con éxito',
    };
  } catch (error) {
    console.error('Error updating medical center requirements', error);
    return {
      type: 'error',
      message: error.message,
    };
  }
}

/**
 * Deletes a requirement from a medical center
 */
export async function deleteMedicalCenterRequirement(id) {
  // TODO
  try {
    const response = await fetch(`${BASE_URL}insurance/company-medical-center-requirement/${id}/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
    });
    if (response.ok) {
      return { type: 'success', message: 'Servicio eliminado con éxito' };
    }
  } catch (error) {
    console.error('Error deleting medical center requirement', error);
  }
}

// Tamaño de página máximo que acepta el endpoint de fondos.
const MEDICAL_CENTER_FUND_PAGE_SIZE = 1000;

/**
 * Get list of fund from medical center
 *
 * Trae TODOS los fondos recorriendo las páginas: antes solo se leía la
 * primera (100) y, con clientes de más de 100 fondos, algunas clínicas no
 * aparecían en los selectores.
 */
export async function getMedicalCenterFund({ clientID, state, forBalance = false, isActive }) {
  // Create query parameters
  const queryParams = new URLSearchParams({ page_size: String(MEDICAL_CENTER_FUND_PAGE_SIZE) });
  if (clientID) {
    queryParams.append('company_client', clientID);
  }
  if (state) {
    queryParams.append('medical_center__state', state);
  }
  if (forBalance) {
    queryParams.append('for_balance', String(forBalance));
  }
  if (isActive !== undefined) {
    queryParams.append('medical_center__is_active', String(isActive));
  }
  try {
    /** @type MedicalCenterFund[] */
    const results = [];
    let page = 1;
    while (page) {
      queryParams.set('page', String(page));
      const response = await fetch(
        `${BASE_URL}insurance/medical-center-fund/?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
        }
      );

      const data = await response.json();
      results.push(...(data.results || []));
      page = data.nextPage;
    }
    return results;
  } catch (error) {
    console.error('Error fetching medical centers', error);
    return [];
  }
}

/**
 * Update medical center fund
 * @param {{ id: number, data: object }} params
 */
export async function updateMedicalCenterFund({ id, data }) {
  try {
    const response = await fetch(`${BASE_URL}insurance/medical-center-fund/${id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Error updating medical center fund');
    }

    const responseData = await response.json().catch(() => null);
    return responseData;
  } catch (error) {
    console.error('Error updating medical center fund', error);
    throw error;
  }
}

/**
 * Get list of medical centers managed by a sub-administrator of a sub-administrator
 * @author gabrielm
 */
export async function getManagedMedicalCenters(subAdministrator = null) {
  try {
    const queryParams = new URLSearchParams();
    if (subAdministrator) {
      queryParams.append('sub_administrator', subAdministrator);
    }
    const response = await fetch(
      `${BASE_URL}insurance/managed-medical-centers/?${queryParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }

    /** @type ManagedMedicalCenter[] */
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching managed medical centers', error);
    return [];
  }
}

/**
 * Checks whether the user can add requirements to a case
 * @author gabrielm
 * @param {number} medicalCenter ID of the medical center
 */
export async function getCanAddRequirementsToCase(medicalCenter) {
  try {
    const response = await fetch(
      `${BASE_URL}insurance/can-add-requirements-medical-center/?medical_center=${medicalCenter}`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }

    /** @type {{can_add_requirements: boolean}} */
    const data = await response.json();
    return data.can_add_requirements ?? false;
  } catch (error) {
    console.error('Error fetching can add requirements to case', error);
    return false;
  }
}

/**
 * Obtiene los centros médicos con la información para facturas
 * @param {number} clientId
 */
export async function getMedicalCentersForInvoice(clientId) {
  const response = await fetch(
    `${BASE_URL}insurance/medical-centers-for-invoice/?client=${clientId}`,
    {
      headers: {
        ...getAuthHeader(),
      },
    }
  );

  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    console.error('Error fetching medical centers for invoice', json);
    throw new Error('Ocurrió un error al obtener los centros médicos');
  }

  /**@type {{results: {id: number, name: string, pending_invoices: number}[]}} */
  const data = await response.json();
  return data.results;
}

/**
 * Obtiene la URL del pie de página (footer) del centro médico.
 * Path: medical-center/get-footer/
 */
export async function getMedicalCenterFooter({ centerId = null, studyId = null } = {}) {
  try {
    const params = new URLSearchParams();
    if (centerId) params.append('medical_center_id', centerId);
    if (studyId) params.append('study_id', studyId);

    // Ajustado a tu path exacto
    const res = await fetch(
      `${BASE_URL}insurance/medical-center/get-footer/?${params.toString()}`,
      {
        headers: getAuthHeader(),
      }
    );

    if (res.ok) {
      const data = await res.json();
      return data.footer;
    }
    return null;
  } catch (err) {
    console.error('Error al cargar footer:', err);
    return null;
  }
}

/**
 * Sube una nueva imagen de pie de página (footer).
 * Path: medical-center/update-footer/
 */
export async function setMedicalCenterFooter({ centerId = null, footerFile }) {
  try {
    if (!footerFile) throw new Error('No se proporcionó ningún archivo');

    const form = new FormData();
    form.append('footer', footerFile);

    const params = new URLSearchParams();
    if (centerId) params.append('medical_center_id', centerId);

    // Ajustado a tu path exacto
    const res = await fetch(
      `${BASE_URL}insurance/medical-center/update-footer/?${params.toString()}`,
      {
        method: 'PATCH',
        headers: getAuthHeader(),
        body: form,
      }
    );

    if (res.ok) {
      const data = await res.json();
      return data.footer;
    }

    const errorData = await res.json();
    throw new Error(errorData.error || 'Error al actualizar footer');
  } catch (err) {
    console.error('Error al actualizar footer:', err);
    throw err;
  }
}

// ============================================================
// PAGE DESIGN — diseño de página tipo Canva
// ============================================================

/**
 * Obtiene el diseño de página (JSON de elementos + URL del PNG exportado).
 * Si centerId tiene prioridad sobre studyId.
 */
export async function getPageDesign({ centerId = null, studyId = null } = {}) {
  try {
    const params = new URLSearchParams();
    if (centerId) params.append('medical_center_id', centerId);
    else if (studyId) params.append('study_id', studyId);

    const res = await fetch(
      `${BASE_URL}insurance/medical-center/page-design/?${params.toString()}`,
      {
        headers: getAuthHeader(),
      }
    );
    if (res.ok) {
      const data = await res.json();
      return {
        pageDesign: data.page_design || null,
        pageTemplateUrl: data.page_template_url || null,
      };
    }
    return { pageDesign: null, pageTemplateUrl: null };
  } catch (err) {
    console.error('Error al cargar page design:', err);
    return { pageDesign: null, pageTemplateUrl: null };
  }
}

/**
 * Guarda el diseño de página (JSON) en el backend.
 */
export async function savePageDesign({ centerId, pageDesignJson }) {
  const params = new URLSearchParams();
  if (centerId) params.append('medical_center_id', centerId);

  const res = await fetch(`${BASE_URL}insurance/medical-center/page-design/?${params.toString()}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ page_design: pageDesignJson }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al guardar page design');
  }
  return res.json();
}

/**
 * Sube el PNG exportado del canvas como page_template.
 * Reutiliza el endpoint de logo para compatibilidad con el generador de PDF existente.
 * También intenta guardar en page_template_url si el backend lo soporta.
 */
export async function uploadPageTemplate({ centerId, pngBlob }) {
  if (!pngBlob) throw new Error('No se proporcionó imagen');
  if (pngBlob.size > 5 * 1024 * 1024) throw new Error('La imagen no puede superar 5MB');

  const form = new FormData();
  form.append('file', pngBlob, 'page_template.png');

  const params = new URLSearchParams();
  if (centerId) params.append('medical_center_id', centerId);

  const res = await fetch(
    `${BASE_URL}insurance/medical-center/page-template/?${params.toString()}`,
    {
      method: 'POST',
      headers: getAuthHeader(),
      body: form,
    }
  );

  if (res.ok) {
    const data = await res.json();
    return data.url;
  }
  const errData = await res.json().catch(() => ({}));
  throw new Error(errData.error || 'Error al subir plantilla');
}

/**
 * Sube una imagen individual al servidor para usarla como elemento en el canvas de diseño.
 * Devuelve la URL pública del archivo subido.
 */
export async function uploadDesignAsset({ centerId, imageFile }) {
  if (!imageFile) throw new Error('No se proporcionó archivo');
  if (!imageFile.type.startsWith('image/')) throw new Error('Solo se permiten imágenes');
  if (imageFile.size > 5 * 1024 * 1024) throw new Error('La imagen no puede superar 5MB');

  const form = new FormData();
  form.append('file', imageFile);

  const params = new URLSearchParams();
  if (centerId) params.append('medical_center_id', centerId);

  const res = await fetch(
    `${BASE_URL}insurance/medical-center/design-asset/?${params.toString()}`,
    {
      method: 'POST',
      headers: getAuthHeader(),
      body: form,
    }
  );

  if (res.ok) {
    const data = await res.json();
    return data.url;
  }
  const errData = await res.json().catch(() => ({}));
  throw new Error(errData.error || 'Error al subir imagen');
}
